/** The Dryer: every lot at one drying facility, from any truck. */
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { ListPageSkeleton } from "@/components/app/skeletons";
import { Callout } from "@/components/ui/callout";
import { useAuth } from "@/features/auth/store";
import { useFacilities } from "@/features/auth/use-facilities";
import { DryerPage } from "@/features/dryer/components/dryer-page";
import { useDryers } from "@/features/dryer/hooks";

const search = z.object({
  tab: z
    // "Sent to plant" was split into In Transit and Delivered (user 2026-10-06); old links land on In Transit.
    .preprocess(
      (t) => (t === "sent" ? "transit" : t),
      z.enum([
        "waiting",
        "blending",
        "blended",
        "drying",
        "ready",
        "transit",
        "delivered",
        "cancelled",
      ])
    )
    .default("waiting"),
  q: z.string().optional(),
  facility: z.coerce.number().optional(),
  page: z.number().int().min(1).default(1),
});

export const Route = createFileRoute("/_app/dryer/")({
  validateSearch: search,
  beforeLoad: () => {
    if (!useAuth.getState().can("document.view")) throw redirect({ to: "/" });
  },
  component: Page,
});

function Page() {
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const dryers = useDryers();
  const { selected } = useFacilities();
  if (dryers.isPending) return <ListPageSkeleton />;
  const list = dryers.data ?? [];
  // The header's plant when it is a dryer, else the one asked for, else the first.
  const dryer =
    list.find((d) => d.id === s.facility) ??
    list.find((d) => d.id === selected?.id) ??
    list[0];
  if (!dryer)
    return (
      <Callout tone="info" title="No dryer">
        None of your plants is a drying facility.
      </Callout>
    );
  return (
    <DryerPage
      facilityId={dryer.id}
      facilityName={dryer.name}
      search={s}
      onSearch={(next) =>
        // Any change but the page's own starts again at page 1.
        void navigate({ search: (prev) => ({ ...prev, page: 1, ...next }) })
      }
    />
  );
}
